import { cn } from '../utils/cn.js';

export interface StepperStep {
  /** Step title, shown next to the number. */
  label: string;
  /** Small tag above the label (e.g. "Step 2"). Auto-computed if omitted. */
  eyebrow?: string;
  /** Explicit state override. If omitted, derived from `current`. */
  state?: 'todo' | 'current' | 'done' | 'error';
  /** Optional key. Falls back to array index. */
  key?: string;
}

export interface StepperProps {
  steps: StepperStep[];
  /** 1-indexed step number that is currently active. */
  current: number;
  className?: string;
  ariaLabel?: string;
}

/**
 * `<Stepper>` — wraps the `.stepper` CSS from kollez.css. Consolidates the
 * three page-level implementations in RegisterStudent, RegisterInstitution
 * and AcceptInvite.
 *
 * States on each `<li>`:
 *   data-state="done"     completed step
 *   data-state="current"  active step  (also aria-current="step")
 *   data-state="error"    step user came back to and left invalid
 *   (unset)               upcoming
 */
export function Stepper({ steps, current, className, ariaLabel = 'Progress' }: StepperProps) {
  return (
    <ol className={cn('stepper', className)} aria-label={ariaLabel}>
      {steps.map((s, i) => {
        const oneIndex = i + 1;
        const state =
          s.state ??
          (oneIndex < current
            ? 'done'
            : oneIndex === current
              ? 'current'
              : undefined);
        return (
          <li
            key={s.key ?? i}
            data-state={state ?? ''}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="step-n">{s.eyebrow ?? `Step ${oneIndex}`}</span>
            {s.label}
          </li>
        );
      })}
    </ol>
  );
}
